import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IconChevronDown, IconX, IconSearch } from '@tabler/icons-react'

import { useGetPermissionsCatalog, useGetRoles, useReplaceMemberRoles } from '@queries/Roles'
import { requestErrorsHandler } from '@helpers/utils'
import { useAnchoredPosition } from '@helpers/hooks/useAnchoredPosition'
import useGetIsRolesTabEnabled from '@helpers/hooks/useGetIsRolesTabEnabled'

import { useAdminExclusiveRoles } from '@helpers/hooks/useAdminExclusiveRoles'

import type { Role, PermissionCatalogEntry } from '@models/Role'
import type { RoleInfo } from '@type/permissions'

const POPOVER_WIDTH = 288

export interface RoleChipsProps {
  /** TeamUser id to assign/unassign roles on. */
  teamUserId: number
  /** Currently-assigned roles for this user. */
  assignedRoles: RoleInfo[]
  /**
   * When true, the chips render read-only — no x to remove and no
   * "+ Add role" affordance. Used for the row representing the current
   * viewer (a user can't change their own roles) and for non-admin
   * viewers.
   */
  readOnly?: boolean
}

/**
 * Chip-style multi-role display + assignment widget. Each assigned
 * role renders as a chip with an "x" to remove (unless it would leave
 * the user with zero roles — backend rejects that too). The picker
 * opens a checkbox list so multiple roles can be toggled in one open
 * session, then PUT /api/roles/members/{id}/ reconciles the user's
 * full role set in one transaction on close (or on explicit Apply).
 */
export const RoleChips: React.FC<RoleChipsProps> = ({ teamUserId, assignedRoles, readOnly = false }) => {
  const { data: allRoles } = useGetRoles()
  // Premium gate (mirrors the locked Roles tab; server-enforced in the
  // member-roles endpoints): with the feature off, the picker offers the
  // pre-RBAC Admin-or-Member binary with single-select semantics. Custom
  // roles the member already holds (grandfathered) stay listed so they
  // can be unchecked, but new ones cannot be added.
  const rolesFeatureEnabled = useGetIsRolesTabEnabled()
  const pickerRoles = useMemo(() => {
    if (!allRoles) return allRoles
    if (rolesFeatureEnabled) return allRoles
    return allRoles.filter((r) => r.is_system || assignedRoles.some((a) => a.id === r.id))
  }, [allRoles, rolesFeatureEnabled, assignedRoles])
  const [pickerOpen, setPickerOpen] = useState(false)
  // Trigger ref is used to compute the popover's viewport-relative
  // coords (the popover renders into a portal so it isn't clipped by
  // parent overflow on the Members table). Outside-click resolution
  // uses BOTH the trigger and the portaled popover via instance refs
  // — see InvitePicker below.
  const triggerRef = useRef<HTMLDivElement>(null)
  // Single shared mutation lifted to the row. Each Chip used to own
  // its own useReplaceMemberRoles instance + isLoading flag, which let
  // two quick removals submit conflicting role_ids snapshots computed
  // from the same stale assignedRoles — last response wins, the
  // intermediate remove gets silently undone. Sharing one mutation
  // gives us one isLoading we can use to disable every chip's X while
  // a write is in flight, so the user can't queue a second mutation
  // off stale state.
  const { mutateAsync: replaceRoles, isLoading: isReplacing } = useReplaceMemberRoles(teamUserId)

  // Read-only: just the chips, no trigger surface.
  if (readOnly) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {assignedRoles.map((role) => (
          <Chip
            key={role.id}
            role={role}
            assignedRoles={assignedRoles}
            canRemove={false}
            replaceRoles={replaceRoles}
            disabled={isReplacing}
          />
        ))}
        {assignedRoles.length === 0 && <span className="text-xs text-gray-400">No roles</span>}
      </div>
    )
  }

  if (!allRoles || allRoles.length === 0) {
    // Roles haven't loaded yet — show chips without the trigger so the
    // row doesn't look interactive while it's actually inert.
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {assignedRoles.map((role) => (
          <Chip
            key={role.id}
            role={role}
            assignedRoles={assignedRoles}
            canRemove={false}
            replaceRoles={replaceRoles}
            disabled={isReplacing}
          />
        ))}
      </div>
    )
  }

  // Editable: the whole chips row IS the trigger. Clicking the row
  // (anywhere outside an inner X button) opens the picker. The chevron
  // at the end is a visual affordance — no separate "+ Edit roles"
  // button. Empty state shows a single "+ Add role" placeholder chip
  // so the cell still has a clickable target.
  // Outer trigger is a div (not a button) because chips embed an inner
  // X button — nested buttons are invalid HTML. Keyboard-accessible
  // via role + tabIndex + onKeyDown.
  return (
    <>
      <div
        ref={triggerRef}
        role="button"
        aria-label="Edit roles"
        tabIndex={0}
        onClick={() => setPickerOpen((open) => !open)}
        onKeyDown={(e) => {
          // Ignore Enter/Space that originated on a child (the remove
          // button); the browser fires its own click + keydown there
          // and we don't want both to also toggle the picker.
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setPickerOpen((open) => !open)
          }
        }}
        className="inline-flex flex-wrap items-center gap-1.5 px-1 py-0.5 rounded-md hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand_primary"
      >
        {assignedRoles.length === 0 ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-dashed border-gray-300 text-gray-500">
            + Add role
          </span>
        ) : (
          assignedRoles.map((role) => (
            <Chip
              key={role.id}
              role={role}
              assignedRoles={assignedRoles}
              // Locked mode: a system chip's X would submit a set without
              // any base role (e.g. Admin removed, grandfathered custom
              // kept) — the backend gate rejects that, so don't offer it.
              // Base-role changes go through the picker's radio swap.
              canRemove={assignedRoles.length > 1 && (rolesFeatureEnabled || !role.is_system)}
              replaceRoles={replaceRoles}
              disabled={isReplacing}
            />
          ))
        )}
        <IconChevronDown size={12} className="text-gray-400 ml-0.5" />
      </div>
      {pickerOpen && (
        <RolePicker
          allRoles={pickerRoles ?? allRoles}
          assignedRoles={assignedRoles}
          replaceRoles={replaceRoles}
          isReplacing={isReplacing}
          triggerRef={triggerRef}
          onClose={() => setPickerOpen(false)}
          singleSystemRole={!rolesFeatureEnabled}
        />
      )}
    </>
  )
}

interface ChipProps {
  role: RoleInfo
  assignedRoles: RoleInfo[]
  canRemove: boolean
  /**
   * Shared mutation lifted from the parent row. See the comment on
   * RoleChips for why each chip no longer owns its own.
   */
  replaceRoles: (body: { role_ids: number[] }) => Promise<unknown>
  /**
   * True while any chip on the row is replacing; disables every X
   * button so the user can't queue conflicting removals.
   */
  disabled: boolean
}

const Chip: React.FC<ChipProps> = ({ role, assignedRoles, canRemove, replaceRoles, disabled }) => {
  const handleRemove = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      const remaining = assignedRoles.filter((r) => r.id !== role.id).map((r) => r.id)
      try {
        await replaceRoles({ role_ids: remaining })
      } catch (err) {
        // Backend rejects if removal would leave the user with zero
        // roles. Surface via the standard toast handler.
        requestErrorsHandler(err)
      }
    },
    [replaceRoles, assignedRoles, role.id],
  )

  const isSystem = role.is_system
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md max-w-full ${
        isSystem
          ? 'bg-blue-50 text-blue-700 border border-blue-200'
          : 'bg-purple-50 text-purple-700 border border-purple-200'
      }`}
    >
      {/* Truncate long role names so one chip can't blow out the column. */}
      <span className="truncate max-w-[160px]" title={role.name}>
        {role.name}
      </span>
      {canRemove && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          className="opacity-60 hover:opacity-100 disabled:opacity-30"
          aria-label={`Remove ${role.name}`}
        >
          <IconX size={12} />
        </button>
      )}
    </span>
  )
}

interface RolePickerProps {
  allRoles: Role[]
  assignedRoles: RoleInfo[]
  /**
   * Shared mutation lifted from the row — the SAME instance the chip X
   * buttons use. One `isReplacing` covers both Apply and chip-removes, so a
   * second write can't be queued off a stale snapshot while one is in flight.
   */
  replaceRoles: (body: { role_ids: number[] }) => Promise<unknown>
  isReplacing: boolean
  triggerRef: React.RefObject<HTMLDivElement | null>
  onClose: () => void
  /**
   * Premium gate: system roles behave as radio buttons (picking Admin
   * deselects Member and vice versa; the held one can't be unchecked).
   * The parent has already filtered the role list to system + the
   * member's grandfathered customs. Server-enforced independently.
   */
  singleSystemRole?: boolean
}

const RolePicker: React.FC<RolePickerProps> = ({
  allRoles,
  assignedRoles,
  replaceRoles,
  isReplacing,
  triggerRef,
  onClose,
  singleSystemRole = false,
}) => {
  // NOTE: the rest of this popover still uses hardcoded English (pre-existing);
  // new copy goes through i18n.
  const { t } = useTranslation('org')
  const { data: catalog } = useGetPermissionsCatalog()

  // Local selection seeded from current assignments. User toggles
  // freely, then "Apply" commits via the reconciler endpoint. Cancel /
  // outside-click discards local changes.
  const [picked, setPicked] = useState<Set<number>>(() => new Set(assignedRoles.map((r) => r.id)))
  const [search, setSearch] = useState('')

  // Portal escapes the Members table's `overflow: auto` clip. Outside-
  // click resolution checks BOTH the trigger and the portaled popover
  // since they live in different parts of the DOM tree.
  const popoverRef = useRef<HTMLDivElement>(null)
  const position = useAnchoredPosition(triggerRef, {
    open: true,
    align: 'left',
    popoverWidth: POPOVER_WIDTH,
    // Lets the hook flip the popover above the trigger when it would not
    // fit below, and clamp it inside the viewport. Without this a row near
    // the bottom of the table opened a popover whose list and footer were
    // cut off, with no way to scroll to them.
    popoverRef,
  })

  // True while the Admin-swap confirmation is open. That dialog renders
  // outside this popover, so its buttons would otherwise register as an
  // outside-click, close the picker mid-await, and drop the resolved
  // selection on the floor.
  const confirmPendingRef = useRef(false)

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (confirmPendingRef.current) return
      // MouseEvent.target is `EventTarget | null` which is broader
      // than the `Node` Element.contains() requires. Runtime guard
      // narrows safely; non-Node targets (e.g. window in some
      // synthetic-event cases) fall through to the close branch.
      if (!(e.target instanceof Node)) {
        onClose()
        return
      }
      const target = e.target
      const insideTrigger = triggerRef.current?.contains(target)
      const insidePopover = popoverRef.current?.contains(target)
      if (!insideTrigger && !insidePopover) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [onClose, triggerRef])

  const codenameToLabel = useMemo(() => {
    const map = new Map<string, string>()
    catalog?.forEach((e: PermissionCatalogEntry) => map.set(e.codename, e.label))
    return map
  }, [catalog])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allRoles
    return allRoles.filter((r) => r.name.toLowerCase().includes(q))
  }, [allRoles, search])

  const systemIds = useMemo(() => new Set(allRoles.filter((r) => r.is_system).map((r) => r.id)), [allRoles])

  // Admin is exclusive when multi-role is unlocked — see useAdminExclusiveRoles.
  // Locked (singleSystemRole) uses radio semantics for the Admin/Member pair
  // instead: no demotion prompt, held system role can't be unchecked.
  const { isAdminSelected, resolveToggle } = useAdminExclusiveRoles(allRoles)
  const isAdminPicked = !singleSystemRole && isAdminSelected([...picked])

  const toggle = useCallback(
    async (role: Role) => {
      if (singleSystemRole) {
        setPicked((prev) => {
          const next = new Set(prev)
          if (systemIds.has(role.id)) {
            // Radio semantics for the Admin/Member pair when the Roles
            // feature is off: picking one drops the other; the held one
            // can't be unchecked (a member always keeps one base role).
            if (next.has(role.id)) return prev
            for (const sid of systemIds) next.delete(sid)
            next.add(role.id)
            return next
          }
          // Grandfathered custom roles still toggle freely.
          if (next.has(role.id)) next.delete(role.id)
          else next.add(role.id)
          return next
        })
        return
      }

      // One toggle at a time. `picked` is captured at render, so a second
      // chip clicked while the swap confirmation is open would resolve
      // against the pre-dialog selection and overwrite whatever the first
      // toggle settled on.
      if (confirmPendingRef.current) return

      confirmPendingRef.current = true
      try {
        const resolved = await resolveToggle([...picked], role)
        // null = the user declined replacing Admin; keep the current selection.
        if (resolved === null) return
        setPicked(new Set(resolved))
      } finally {
        confirmPendingRef.current = false
      }
    },
    [picked, resolveToggle, singleSystemRole, systemIds],
  )

  const handleApply = useCallback(async () => {
    if (picked.size === 0) return
    try {
      await replaceRoles({ role_ids: [...picked] })
      onClose()
    } catch (err) {
      requestErrorsHandler(err)
    }
  }, [picked, replaceRoles, onClose])

  // Dirty = local selection diverges from current assignment.
  const isDirty = useMemo(() => {
    const current = new Set(assignedRoles.map((r) => r.id))
    if (current.size !== picked.size) return true
    for (const id of current) if (!picked.has(id)) return true
    return false
  }, [picked, assignedRoles])

  if (!position) return null

  return createPortal(
    <div
      ref={popoverRef}
      // maxHeight is set only when the popover cannot fit in full on
      // either side of the trigger. Applying it with a flex column keeps
      // the search box and the footer pinned while the role list takes the
      // remaining space and scrolls — rather than the popover running off
      // the viewport edge where it cannot be scrolled to at all.
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH, maxHeight: position.maxHeight }}
      className="fixed z-dropdown flex flex-col rounded-md border border-gray-200 bg-white shadow-lg p-2"
    >
      <div className="relative mb-2">
        <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search roles…"
          aria-label="Search roles"
          className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
        />
      </div>

      <div className="max-h-[240px] min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-3 text-xs text-gray-400 text-center">No roles match.</div>
        ) : (
          filtered.map((role) => {
            const checked = picked.has(role.id)
            const previewPerms = role.permissions
              .slice(0, 3)
              .map((p) => codenameToLabel.get(p) ?? p)
              .join(', ')
            const remaining = Math.max(0, role.permissions.length - 3)
            const tooltipText =
              role.permissions.length === 0
                ? 'No permissions'
                : remaining > 0
                ? `${previewPerms}, +${remaining} more`
                : previewPerms
            return (
              <label
                key={role.id}
                title={tooltipText}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(role)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="flex-1 text-sm text-gray-900 truncate">{role.name}</span>
                {role.is_system && (
                  <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">System</span>
                )}
              </label>
            )
          })
        )}
      </div>

      {/* Sets expectations before the click: picking another role while
          Admin is held replaces Admin (confirmed first), never fails. */}
      {isAdminPicked && (
        <p className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500">{t('adminExclusive.hint')}</p>
      )}

      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500">
          {picked.size} role{picked.size === 1 ? '' : 's'} selected
        </span>
        <div className="flex gap-1.5">
          <button type="button" onClick={onClose} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!isDirty || picked.size === 0 || isReplacing}
            className="px-3 py-1 text-xs bg-brand_primary text-white rounded hover:opacity-90 disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default RoleChips
